/**
 * Required field validation utilities for policy form questions
 *
 * This module centralizes validation logic for required fields across all question types,
 * including special handling for table questions with minimum rows and required columns.
 */

export interface RequiredFieldValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

/**
 * Validates if a required question has a valid value.
 * 
 * For regular questions: Checks if the value is non-empty
 * For table questions: Checks minimum rows and required columns
 * 
 * @param question - The form question to validate
 * @param value - The current value to validate
 * @returns Validation result with error message if invalid
 */
export function validateRequiredQuestion(
  question: PolicyFormQuestion,
  value: PolicyFormSubmissionDataValue,
): RequiredFieldValidationResult {
  if (question.type !== 'table') {
    // For non-table questions, use simple empty check
    const isEmpty = value === undefined || value === null || value === '';
    return {
      isValid: !isEmpty,
      errorMessage: isEmpty ? 'This field is required' : undefined,
    };
  }

  const tableData = Array.isArray(value) ? value : [];
  const minRows = question.minRows ?? 1; // Use nullish coalescing to allow minRows: 0

  // Check minimum rows requirement
  if (tableData.length < minRows) {
    return {
      isValid: false,
      errorMessage: `At least ${minRows} row${minRows === 1 ? '' : 's'} required`,
    };
  }

  // Check required columns in each row
  if (question.columns) {
    for (let rowIndex = 0; rowIndex < tableData.length; rowIndex++) {
      const row = tableData[rowIndex];
      if (!row || typeof row !== 'object') {
        // Treat null/undefined/invalid rows as having empty required fields
        const hasRequiredColumns = question.columns.some(column => column.required);
        if (hasRequiredColumns) {
          return {
            isValid: false,
            errorMessage: 'All required fields must be completed in each row',
          };
        }
        continue;
      }
      
      for (const column of question.columns) {
        if (column.required) {
          const cellValue = row[column.id];
          if (cellValue === undefined || cellValue === null || cellValue === '') {
            return {
              isValid: false,
              errorMessage: 'All required fields must be completed in each row',
            };
          }
        }
      }
    }
  }

  return { isValid: true };
}

/**
 * Checks if an error message is related to required field validation
 * (as opposed to format validation or other error types)
 * 
 * @param errorMessage - The error message to check
 * @returns True if the error is about a required field
 */
export function isRequiredFieldError(errorMessage: string | undefined): boolean {
  if (!errorMessage) return false;
  
  return errorMessage === 'This field is required' || 
    errorMessage.endsWith('required') ||
    (errorMessage.includes('row') && errorMessage.includes('required')); // Table-specific messages
}

/**
 * Determines if a table cell should display error styling because it's
 * missing a required value
 * 
 * @param column - The table column definition
 * @param cellValue - The current value in the cell
 * @param tableHasValidationError - Whether the table has any validation errors
 * @returns True if the cell should show error styling
 */
export function isTableCellMissingRequiredValue(
  column: PolicyFormTableColumn,
  cellValue: any,
  tableHasValidationError: boolean,
): boolean {
  return (
    tableHasValidationError &&
    !!column.required &&
    (cellValue === undefined || cellValue === null || cellValue === '')
  );
}